import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useSWR from 'swr'
import {
  Briefcase,
  Check,
  ChevronDown,
  ChevronRight,
  Globe,
  Loader2,
  Search,
  SlidersHorizontal,
} from '@digital-twins/design-system/icons'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { CountryFlag } from '@/components/ui/CountryFlag'
import { Text } from '@/components/ui/Text'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { taxonomyApi } from '@/lib/api/client'
import { AudienceSummary } from '@/lib/api'
import { useIndustryTokenMeta } from '@/pages/AudiencesPage/hooks/useIndustryTokenMeta'
import { COUNTRIES } from '@/lib/constants/demographics'
import { localizedCountryName } from '@/lib/utils/localized-country-name'
import { REGIONS } from '@/components/onboarding/steps/market-data'
import {
  getIndustryDisplayName,
  getIndustryIcon,
  isVisibleCategory,
} from '@/lib/constants/industry-categories'
import { filterAudiences } from '@/lib/utils/audience-filter-predicate'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface AudienceFilterState {
  categoryTokens: Array<string>
  countryCodes: Array<string>
  audienceType: 'B2B' | 'B2C' | null
}

interface AudienceFilterModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  state: AudienceFilterState
  onApply: (state: AudienceFilterState, categoryNamesByToken?: Record<string, string>) => void
  audiences: Array<AudienceSummary>
  /** Display names for selected category tokens (from chips / session cache). */
  categoryTokenNames?: Record<string, string>
}

/* ------------------------------------------------------------------ */
/*  Trigger button                                                     */
/* ------------------------------------------------------------------ */

export function FilterTriggerButton({ activeCount, onClick, className }: { activeCount: number; onClick: () => void; className?: string }) {
  const { t } = useTranslation('app')
  return (
    <Button
      variant="outline"
      onClick={onClick}
      data-testid="audience-filter-trigger"
      className={cn(
        'h-10 rounded-lg px-4 gap-2 text-sm font-medium',
        activeCount > 0 && 'border-primary/40 bg-primary/5',
        className,
      )}
    >
      <SlidersHorizontal className="h-4 w-4" />
      {t('audiences.filters')}
      {activeCount > 0 && (
        <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-bold text-primary-foreground">
          {activeCount}
        </span>
      )}
    </Button>
  )
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function countryLabel(code: string, locale: string): string {
  return localizedCountryName(code, locale)
}

/* ------------------------------------------------------------------ */
/*  Country dropdown (Popover-based, scroll-fixed)                    */
/* ------------------------------------------------------------------ */

interface CountryDropdownProps {
  selectedCountryCodes: Array<string>
  onToggleCountry: (code: string) => void
  onToggleRegion: (codes: Array<string>) => void
}

function CountryDropdown({ selectedCountryCodes, onToggleCountry, onToggleRegion }: CountryDropdownProps) {
  const { t, i18n } = useTranslation('app')
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [activeRegion, setActiveRegion] = useState<string | null>(null)

  const selectedCountrySet = useMemo(() => new Set(selectedCountryCodes), [selectedCountryCodes])

  const countrySummary = useMemo(() => {
    if (selectedCountryCodes.length === 0) return undefined
    const names = selectedCountryCodes.map((code) => countryLabel(code, i18n.language))
    return names.length <= 2 ? names.join(', ') : `${names.slice(0, 2).join(', ')} +${names.length - 2}`
  }, [selectedCountryCodes, i18n.language])

  const handleClose = useCallback(() => {
    setOpen(false)
    setSearch('')
    setActiveRegion(null)
  }, [])

  const q = search.toLowerCase()
  const filteredRegions = useMemo(() => {
    if (!q) return REGIONS
    return REGIONS.map((r) => ({
      ...r,
      countries: r.countries.filter((code) =>
        countryLabel(code, i18n.language).toLowerCase().includes(q) || code.toLowerCase().includes(q),
      ),
    })).filter((r) => r.countries.length > 0)
  }, [q, i18n.language])

  const activeRegionData = useMemo(() => {
    if (!activeRegion) return null
    return filteredRegions.find((r) => r.id === activeRegion) ?? null
  }, [activeRegion, filteredRegions])

  const columnCount = activeRegion ? 2 : 1
  const popoverWidth = columnCount * 220

  return (
    <Popover open={open} onOpenChange={(next) => { if (!next) handleClose(); else setOpen(true) }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="country-selector"
          className={cn(
            'flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left transition-smooth',
            'border border-input hover:border-primary/30',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
            selectedCountryCodes.length > 0 ? 'bg-primary/4' : 'bg-background',
          )}
        >
          <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
          <Text as="span" size="sm" className="text-foreground font-medium flex-1 truncate">
            {countrySummary ? t('audiences.countryPrefixed', { summary: countrySummary }) : t('audiences.country')}
          </Text>
          <ChevronDown className={cn('h-3.5 w-3.5 text-muted-foreground shrink-0 transition-transform', open && 'rotate-180')} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="p-0 rounded-xl overflow-hidden transition-[width] duration-200"
        style={{
          width: `${popoverWidth}px`,
          maxHeight: 'min(400px, var(--radix-popover-content-available-height))',
        }}
      >
        {/* Search bar */}
        <div className="px-3 py-2 border-b border-border/50 shrink-0">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/60" />
            <Input
              autoFocus
              placeholder={t('audiences.searchEllipsis')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 pl-8 text-sm rounded-md bg-transparent border-0 focus-visible:ring-0 placeholder:text-muted-foreground/50"
            />
          </div>
        </div>

        {/* Column-view body */}
        <div
          className="flex overflow-x-hidden"
          style={{ maxHeight: 'calc(min(400px, var(--radix-popover-content-available-height)) - 49px)' }}
        >
          {/* Left column: regions/continents */}
          <div className="flex flex-col w-[220px] shrink-0 border-r border-border/40 last:border-r-0">
            <div
              className="overflow-y-auto overscroll-contain scrollbar-overlay p-1.5 flex-1 min-h-0"
              onWheel={(e) => e.stopPropagation()}
            >
              {filteredRegions.length === 0 ? (
                <Text size="xs" color="muted" className="py-4 text-center block">No matches</Text>
              ) : (
                <div className="space-y-0.5">
                  {filteredRegions.map((region) => {
                    const allSel = region.countries.every((c) => selectedCountrySet.has(c))
                    const someSel = !allSel && region.countries.some((c) => selectedCountrySet.has(c))
                    const isActive = activeRegion === region.id
                    return (
                      <button
                        key={region.id}
                        type="button"
                        data-testid={`country-region-${region.id}`}
                        onClick={() => setActiveRegion(isActive ? null : region.id)}
                        className={cn(
                          'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left transition-colors',
                          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/20',
                          isActive
                            ? 'bg-accent'
                            : someSel || allSel ? 'bg-primary/6' : 'hover:bg-accent/60',
                        )}
                      >
                        <span
                          role="presentation"
                          onClick={(e) => { e.stopPropagation(); onToggleRegion(region.countries) }}
                          className={cn(
                            'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border transition-colors',
                            allSel ? 'border-primary bg-primary text-primary-foreground'
                              : someSel ? 'border-primary/50 bg-primary/15 text-primary'
                              : 'border-muted-foreground/30',
                          )}
                        >
                          {(allSel || someSel) && <Check className="h-2 w-2" strokeWidth={3} />}
                        </span>
                        <Text as="span" size="xs" className={cn('flex-1 truncate', (isActive || allSel) && 'font-medium')}>
                          {region.label}
                        </Text>
                        <ChevronRight className={cn('h-3 w-3 shrink-0 transition-colors', isActive ? 'text-foreground' : 'text-muted-foreground/30')} />
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right column: countries in selected region */}
          {activeRegionData && (
            <div className="flex flex-col w-[220px] shrink-0">
              <div className="px-3 py-1.5 border-b border-border/40 shrink-0">
                <Text size="xs" color="muted" className="truncate font-medium">
                  {activeRegionData.label}
                </Text>
              </div>
              <div
                className="overflow-y-auto overscroll-contain scrollbar-overlay p-1.5 flex-1 min-h-0"
                onWheel={(e) => e.stopPropagation()}
              >
                <div className="space-y-0.5">
                  {/* Select all row */}
                  {(() => {
                    const allSel = activeRegionData.countries.every((c) => selectedCountrySet.has(c))
                    return (
                      <button
                        type="button"
                        onClick={() => onToggleRegion(activeRegionData.countries)}
                        className={cn(
                          'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left transition-colors',
                          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/20',
                          allSel ? 'bg-primary/6' : 'hover:bg-accent/60',
                        )}
                      >
                        <span className={cn(
                          'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border transition-colors',
                          allSel ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/30',
                        )}>
                          {allSel && <Check className="h-2 w-2" strokeWidth={3} />}
                        </span>
                        <Text as="span" size="xs" className="flex-1 truncate italic text-muted-foreground">
                          {t('audiences.selectAll')}
                        </Text>
                      </button>
                    )
                  })()}
                  {activeRegionData.countries.map((code) => {
                    const sel = selectedCountrySet.has(code)
                    return (
                      <button
                        key={code}
                        type="button"
                        data-testid={`country-option-${code}`}
                        onClick={() => onToggleCountry(code)}
                        className={cn(
                          'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left transition-colors',
                          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/20',
                          sel ? 'bg-primary/6' : 'hover:bg-accent/60',
                        )}
                      >
                        <span className={cn(
                          'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border transition-colors',
                          sel ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/30',
                        )}>
                          {sel && <Check className="h-2 w-2" strokeWidth={3} />}
                        </span>
                        <CountryFlag code={code} />
                        <Text as="span" size="xs" className={cn('flex-1 truncate', sel && 'font-medium')}>
                          {countryLabel(code, i18n.language)}
                        </Text>
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

/* ------------------------------------------------------------------ */
/*  Single column inside the category column-view                     */
/* ------------------------------------------------------------------ */

interface CategoryColumnProps {
  parentToken: string | null
  parentName: string | null
  selectedSet: Set<string>
  activeChildToken: string | null
  onToggle: (token: string) => void
  onDrill: (token: string, name: string) => void
  isRoot: boolean
  search: string
  tokenNameMap: React.MutableRefObject<Map<string, string>>
  onNamesRegistered?: () => void
}

function CategoryColumn({
  parentToken, parentName, selectedSet, activeChildToken,
  onToggle, onDrill, isRoot, search, tokenNameMap, onNamesRegistered,
}: CategoryColumnProps) {
  const { data, isLoading } = useSWR(
    parentToken
      ? `taxonomy-industry-children-${parentToken}`
      : 'taxonomy-industry-tier1-col',
    () => parentToken
      ? taxonomyApi.getNodes('industry', { parentToken })
      : taxonomyApi.getNodes('industry', { tier: 1 }),
    { revalidateOnFocus: false, dedupingInterval: 60_000 },
  )

  const allNodes = isRoot
    ? (data?.nodes ?? []).filter((n) => isVisibleCategory(n.name))
    : (data?.nodes ?? [])

  // Register all loaded token→name pairs into the shared cache
  useEffect(() => {
    if (!data?.nodes) return
    let changed = false
    for (const node of data.nodes) {
      if (!tokenNameMap.current.has(node.token)) {
        tokenNameMap.current.set(node.token, node.name)
        changed = true
      }
    }
    if (changed) onNamesRegistered?.()
  }, [data, tokenNameMap, onNamesRegistered])

  const q = search.toLowerCase()
  const nodes = q ? allNodes.filter((n) => n.name.toLowerCase().includes(q)) : allNodes

  return (
    <div className="flex flex-col w-[220px] shrink-0 border-r border-border/40 last:border-r-0">
      {/* Column header (non-root only) */}
      {!isRoot && parentName && (
        <div className="px-3 py-1.5 border-b border-border/40 shrink-0">
          <Text size="xs" color="muted" className="truncate font-medium">
            {getIndustryDisplayName(parentName) ?? parentName}
          </Text>
        </div>
      )}

      <div
        className="overflow-y-auto overscroll-contain scrollbar-overlay p-1.5 flex-1 min-h-0"
        onWheel={(e) => e.stopPropagation()}
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : nodes.length === 0 ? (
          <Text size="xs" color="muted" className="py-4 text-center block">No matches</Text>
        ) : (
          <div className="space-y-0.5">
            {/* Parent "Select all" row at top of child columns */}
            {!isRoot && !q && parentToken && (() => {
              const sel = selectedSet.has(parentToken)
              return (
                <button
                  type="button"
                  onClick={() => onToggle(parentToken)}
                  data-testid={`category-select-all-${parentToken}`}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left transition-colors',
                    'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/20',
                    sel ? 'bg-primary/6' : 'hover:bg-accent/60',
                  )}
                >
                  <span className={cn(
                    'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border transition-colors',
                    sel ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/30',
                  )}>
                    {sel && <Check className="h-2 w-2" strokeWidth={3} />}
                  </span>
                  <Text as="span" size="xs" className="flex-1 truncate italic text-muted-foreground">
                    All
                  </Text>
                </button>
              )
            })()}
            {nodes.map((node) => {
              const sel = selectedSet.has(node.token)
              // tier-1 nodes always have children (API hasChildren flag is unreliable)
              const hasKids = isRoot ? true : node.hasChildren
              const isActive = activeChildToken === node.token
              const Icon = isRoot ? getIndustryIcon(node.name) : null
              const displayName = isRoot
                ? (getIndustryDisplayName(node.name) ?? node.name)
                : node.name
              return (
                <button
                  key={node.token}
                  type="button"
                  data-testid={isRoot ? `category-tier1-${node.token}` : `category-child-${node.token}`}
                  onClick={() => {
                    if (hasKids) {
                      onDrill(node.token, node.name)
                    } else {
                      onToggle(node.token)
                    }
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left transition-colors',
                    'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/20',
                    isActive
                      ? 'bg-accent'
                      : sel ? 'bg-primary/6' : 'hover:bg-accent/60',
                  )}
                >
                  {/* Checkbox */}
                  <span
                    role="presentation"
                    onClick={(e) => { e.stopPropagation(); onToggle(node.token) }}
                    className={cn(
                      'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border transition-colors',
                      sel ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/30',
                    )}
                  >
                    {sel && <Check className="h-2 w-2" strokeWidth={3} />}
                  </span>
                  {Icon && <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />}
                  <Text as="span" size="xs" className={cn('flex-1 truncate', (sel || isActive) && 'font-medium')}>
                    {displayName}
                  </Text>
                  {hasKids && (
                    <ChevronRight className={cn('h-3 w-3 shrink-0 transition-colors', isActive ? 'text-foreground' : 'text-muted-foreground/30')} />
                  )}
                </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Category dropdown — trigger + column-view popover                 */
/* ------------------------------------------------------------------ */

interface DrillLevel {
  token: string
  name: string
}

export interface CategoryDropdownProps {
  selectedTokens: Array<string>
  onToggle: (token: string) => void
  categorySummary?: string
  tokenNameMap: React.MutableRefObject<Map<string, string>>
  onNamesRegistered?: () => void
  compact?: boolean
  renderTrigger?: React.ReactNode
}

export function CategoryDropdown({ selectedTokens, onToggle, categorySummary, tokenNameMap, onNamesRegistered, compact, renderTrigger }: CategoryDropdownProps) {
  const { t } = useTranslation('app')
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  // Stack of drilled levels: [{token, name}, ...] deepest last
  const [drillStack, setDrillStack] = useState<Array<DrillLevel>>([])

  const selectedSet = useMemo(() => new Set(selectedTokens), [selectedTokens])

  const handleClose = useCallback(() => {
    setOpen(false)
    setSearch('')
    setDrillStack([])
  }, [])

  const handleDrill = useCallback((token: string, name: string, depth: number) => {
    // Replace everything after this depth and push new level
    setDrillStack((prev) => [...prev.slice(0, depth), { token, name }])
    setSearch('')
  }, [])

  // Columns: root + one per drilled level
  const columnCount = 1 + drillStack.length
  const popoverWidth = Math.min(columnCount, 3) * 220  // cap at 3 cols visible

  return (
    <Popover open={open} onOpenChange={(next) => { if (!next) handleClose(); else setOpen(true) }}>
      <PopoverTrigger asChild>
        {renderTrigger ?? (
          <button
            type="button"
            data-testid="category-selector"
            className={cn(
              'flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left transition-smooth',
              'border border-input hover:border-primary/30',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
              selectedTokens.length > 0 ? 'bg-primary/4' : 'bg-background',
            )}
          >
            <Briefcase className="h-4 w-4 text-muted-foreground shrink-0" />
            <Text as="span" size="sm" className="text-foreground font-medium flex-1 truncate">
              {categorySummary ? t('audiences.industryPrefixed', { summary: categorySummary }) : t('audiences.industry')}
            </Text>
            <ChevronDown className={cn('h-3.5 w-3.5 text-muted-foreground shrink-0 transition-transform', open && 'rotate-180')} />
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="p-0 rounded-xl overflow-hidden transition-[width] duration-200"
        style={{
          width: `${popoverWidth}px`,
          maxHeight: 'min(400px, var(--radix-popover-content-available-height))',
        }}
      >
        {/* Search bar */}
        <div className="px-3 py-2 border-b border-border/50 shrink-0">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/60" />
            <Input
              autoFocus
              placeholder={t('audiences.searchEllipsis')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 pl-8 text-sm rounded-md bg-transparent border-0 focus-visible:ring-0 placeholder:text-muted-foreground/50"
            />
          </div>
        </div>

        {/* Column-view body */}
        <div
          className="flex overflow-x-hidden"
          style={{ maxHeight: 'calc(min(400px, var(--radix-popover-content-available-height)) - 49px)' }}
        >
          {/* Root column (tier-1) */}
          <CategoryColumn
            parentToken={null}
            parentName={null}
            selectedSet={selectedSet}
            activeChildToken={drillStack[0]?.token ?? null}
            onToggle={onToggle}
            onDrill={(token, name) => handleDrill(token, name, 0)}
            isRoot
            search={search}
            tokenNameMap={tokenNameMap}
            onNamesRegistered={onNamesRegistered}
          />

          {/* Drilled columns */}
          {drillStack.map((level, idx) => (
            <CategoryColumn
              key={level.token}
              parentToken={level.token}
              parentName={level.name}
              selectedSet={selectedSet}
              activeChildToken={drillStack[idx + 1]?.token ?? null}
              onToggle={onToggle}
              onDrill={(token, name) => handleDrill(token, name, idx + 1)}
              isRoot={false}
              search={search}
              tokenNameMap={tokenNameMap}
              onNamesRegistered={onNamesRegistered}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

/* ------------------------------------------------------------------ */
/*  Main modal                                                         */
/* ------------------------------------------------------------------ */

export function AudienceFilterModal({
  open,
  onOpenChange,
  state,
  onApply,
  audiences,
  categoryTokenNames = {},
}: AudienceFilterModalProps) {
  const { t, i18n } = useTranslation('app')
  const [draft, setDraft] = useState<AudienceFilterState>(state)

  const prevOpenRef = useRef(open)
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setDraft(state)
    }
    prevOpenRef.current = open
  }, [open, state])

  /* Category data for match count + summaries ---------------------- */
  const { data: tier1Data } = useSWR(
    'taxonomy-industry-tier1',
    () => taxonomyApi.getNodes('industry', { tier: 1 }),
    { revalidateOnFocus: false, dedupingInterval: 60_000 },
  )
  const tier1Nodes = tier1Data?.nodes ?? []

  const tokenMeta = useIndustryTokenMeta(
    audiences,
    tier1Nodes,
    draft.categoryTokens,
    categoryTokenNames,
  )

  // Shared token→name cache — seeded from tokenMeta + tier-1, extended by CategoryColumn as user drills
  const tokenNameMap = useRef<Map<string, string>>(new Map())
  const [tokenNameVersion, setTokenNameVersion] = useState(0)

  useEffect(() => {
    let changed = false
    for (const node of tier1Nodes) {
      if (!tokenNameMap.current.has(node.token)) {
        tokenNameMap.current.set(node.token, node.name)
        changed = true
      }
    }
    for (const [token, meta] of tokenMeta) {
      if (!tokenNameMap.current.has(token) || tokenNameMap.current.get(token) === token) {
        tokenNameMap.current.set(token, meta.name)
        changed = true
      }
    }
    if (changed) setTokenNameVersion((v) => v + 1)
  }, [tier1Nodes, tokenMeta])

  const resolveTokenName = useCallback(
    (token: string): string => {
      const metaName = tokenMeta.get(token)?.name
      const raw = metaName ?? tokenNameMap.current.get(token) ?? token
      return getIndustryDisplayName(raw) ?? raw
    },
    [tokenMeta, tokenNameVersion],
  )

  const selectedCatSet = useMemo(() => new Set(draft.categoryTokens), [draft.categoryTokens])
  const selectedCountrySet = useMemo(() => new Set(draft.countryCodes), [draft.countryCodes])

  /* Mutations ----------------------------------------------------- */

  const handleToggleCategory = useCallback((token: string) => {
    setDraft((prev) => ({
      ...prev,
      categoryTokens: prev.categoryTokens.includes(token)
        ? prev.categoryTokens.filter((t) => t !== token)
        : [...prev.categoryTokens, token],
    }))
  }, [])

  const toggleCountry = useCallback((code: string) => {
    setDraft((prev) => ({
      ...prev,
      countryCodes: prev.countryCodes.includes(code)
        ? prev.countryCodes.filter((c) => c !== code)
        : [...prev.countryCodes, code],
    }))
  }, [])

  const toggleRegion = useCallback((codes: Array<string>) => {
    setDraft((prev) => {
      const all = codes.every((c) => prev.countryCodes.includes(c))
      if (all) {
        const remove = new Set(codes)
        return { ...prev, countryCodes: prev.countryCodes.filter((c) => !remove.has(c)) }
      }
      return { ...prev, countryCodes: Array.from(new Set([...prev.countryCodes, ...codes])) }
    })
  }, [])

  const clearAll = useCallback(() => {
    setDraft({ categoryTokens: [], countryCodes: [], audienceType: null })
  }, [])

  const apply = useCallback(() => {
    const categoryNamesByToken: Record<string, string> = {}
    for (const token of draft.categoryTokens) {
      const raw = tokenMeta.get(token)?.name ?? tokenNameMap.current.get(token)
      if (raw) {
        categoryNamesByToken[token] = getIndustryDisplayName(raw) ?? raw
      }
    }
    onApply(draft, categoryNamesByToken)
    onOpenChange(false)
  }, [draft, onApply, onOpenChange, tokenMeta])

  const totalActive = draft.categoryTokens.length + draft.countryCodes.length + (draft.audienceType ? 1 : 0)

  const matchCount = useMemo(() => {
    if (totalActive === 0) {
      return audiences.length
    }
    return (
      filterAudiences(audiences, {
        categoryTokens: draft.categoryTokens,
        countryCodes: draft.countryCodes,
        audienceType: draft.audienceType,
      })?.length ?? 0
    )
  }, [audiences, draft, totalActive])

  /* Summaries ----------------------------------------------------- */

  const categorySummary = useMemo(() => {
    if (draft.categoryTokens.length === 0) return undefined
    const names = draft.categoryTokens.map(resolveTokenName)
    return names.length <= 2 ? names.join(', ') : `${names.slice(0, 2).join(', ')} +${names.length - 2}`
  }, [draft.categoryTokens, resolveTokenName])

  const countrySummary = useMemo(() => {
    if (draft.countryCodes.length === 0) return undefined
    const names = draft.countryCodes.map((code) => countryLabel(code, i18n.language))
    return names.length <= 2 ? names.join(', ') : `${names.slice(0, 2).join(', ')} +${names.length - 2}`
  }, [draft.countryCodes, i18n.language])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[480px] p-0 sm:p-0 gap-0 rounded-xl overflow-hidden flex flex-col"
        data-testid="audience-filter-modal"
      >
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
          <DialogTitle className="font-display text-2xl text-foreground tracking-[-0.02em]">
            {t('audiences.filters')}
          </DialogTitle>
        </DialogHeader>

        {/* Pickers */}
        <div className="flex flex-col px-6 pt-3 pb-2 gap-3 overflow-y-auto">
          {/* Audience type — inline toggle row */}
          <div className="flex items-center justify-between py-1">
            <Text as="span" size="sm" className="text-foreground font-medium">
              {t('audiences.audienceType')}
            </Text>
            <div className="flex rounded-full bg-muted/60 p-0.5">
              {([null, 'B2B', 'B2C'] as const).map((type) => {
                const sel = draft.audienceType === type
                return (
                  <button
                    key={type ?? 'any'}
                    type="button"
                    onClick={() => setDraft((prev) => ({ ...prev, audienceType: type }))}
                    data-testid={`audience-type-${(type ?? 'any').toLowerCase()}`}
                    className={cn(
                      'px-4 py-1 text-sm font-medium rounded-full transition-all',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
                      sel
                        ? 'bg-foreground text-background shadow-sm'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {type ?? t('audiences.any')}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Industry */}
          <CategoryDropdown
            selectedTokens={draft.categoryTokens}
            onToggle={handleToggleCategory}
            categorySummary={categorySummary}
            tokenNameMap={tokenNameMap}
            onNamesRegistered={() => setTokenNameVersion((v) => v + 1)}
          />

          {/* Country */}
          <CountryDropdown
            selectedCountryCodes={draft.countryCodes}
            onToggleCountry={toggleCountry}
            onToggleRegion={toggleRegion}
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 pt-4 pb-5 shrink-0">
          <button
            type="button"
            onClick={clearAll}
            disabled={totalActive === 0}
            data-testid="filter-modal-clear"
            className={cn(
              'text-sm font-medium underline underline-offset-4 transition-smooth',
              totalActive > 0 ? 'text-foreground' : 'text-muted-foreground cursor-not-allowed no-underline',
            )}
          >
            {t('audiences.clearAll')}
          </button>
          <Button
            variant="cta"
            shape="pill"
            onClick={apply}
            data-testid="filter-modal-apply"
            className="h-10 px-6 text-base font-medium"
          >
            {matchCount === 0
              ? t('audiences.noMatches')
              : t('audiences.showAudiences', { count: matchCount })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
